<script setup lang="ts">
import { computed } from 'vue';
import gettext from '@/i18n/gettext';
import {
  type AdvancedSettings,
  PART_SYMMETRY_ROTATION_NOT_REQUIRED,
} from '@/models/RobVision';

const { $gettext } = gettext;

const props = defineProps<{
  settings: AdvancedSettings;
  showCADOptions: boolean;
  /** The chosen texture for a CAD part, e.g. "metal" or "custom_color". */
  texture?: string;
  /** Hex color used when texture is "custom_color", e.g. "#FF0000". */
  rgbaValue?: string;
}>();

const isCustomColor = computed(() => props.texture === 'custom_color');

// Only show a color dot for custom colors — named textures (metal, …) have no meaningful rgba.
const swatchColor = computed(() =>
  isCustomColor.value ? props.rgbaValue : undefined,
);

// Repeat angle = the rotation after which the part looks the same from above.
const repeatAngleLabel = computed(() => {
  const symmetry = props.settings.partSymmetry;
  if (symmetry === PART_SYMMETRY_ROTATION_NOT_REQUIRED) {
    return $gettext('Any angle');
  }
  if (symmetry === 360) {
    return $gettext('360° (no repeat before a full turn)');
  }
  return `${symmetry}°`;
});

const textureLabel = computed(() => {
  // When the original texture is kept, the chosen texture/color is ignored.
  if (props.settings.keepTextures) {
    return $gettext('Original');
  }
  if (!props.texture) {
    return $gettext('Overridden');
  }
  if (isCustomColor.value) {
    return props.rgbaValue ?? $gettext('Custom color');
  }
  // Capitalize the texture name, e.g. "metal" -> "Metal".
  return props.texture.charAt(0).toUpperCase() + props.texture.slice(1);
});
</script>

<template>
  <v-card class="pa-4" variant="tonal">
    <v-row dense>
      <v-col v-if="showCADOptions" cols="6" sm="3">
        <div class="setting-item">
          <v-icon class="setting-icon" color="primary">
            mdi-flip-horizontal
          </v-icon>
          <div>
            <div class="text-caption text-medium-emphasis">
              {{ $gettext('Flip') }}
            </div>
            <v-chip
              size="small"
              :color="settings.ropeFlip ? 'success' : 'default'"
              variant="flat"
            >
              {{ settings.ropeFlip ? $gettext('On') : $gettext('Off') }}
            </v-chip>
          </div>
        </div>
      </v-col>

      <v-col v-if="showCADOptions" cols="6" sm="3">
        <div class="setting-item">
          <v-icon class="setting-icon" color="primary">mdi-texture-box</v-icon>
          <div>
            <div class="text-caption text-medium-emphasis">
              {{ $gettext('Texture') }}
            </div>
            <div class="d-flex align-center font-weight-bold">
              <span
                v-if="swatchColor"
                class="texture-swatch"
                :style="{ backgroundColor: swatchColor }"
              ></span>
              {{ textureLabel }}
            </div>
          </div>
        </div>
      </v-col>

      <v-col cols="6" sm="3">
        <div class="setting-item">
          <v-icon class="setting-icon" color="primary">mdi-mirror</v-icon>
          <div>
            <div class="text-caption text-medium-emphasis">
              {{ $gettext('Repeat angle') }}
            </div>
            <div class="font-weight-bold">{{ repeatAngleLabel }}</div>
          </div>
        </div>
      </v-col>

      <!-- Only after a deprecated Mask R-CNN training; the current model has no
           rotation classifier step. -->
      <v-col v-if="settings.resolutionAngle != null" cols="6" sm="3">
        <div class="setting-item">
          <v-icon class="setting-icon" color="primary">mdi-rotate-right</v-icon>
          <div>
            <div class="text-caption text-medium-emphasis">
              {{ $gettext('Resolution angle') }}
            </div>
            <div class="font-weight-bold">{{ settings.resolutionAngle }}°</div>
          </div>
        </div>
      </v-col>
    </v-row>
  </v-card>
</template>

<style scoped>
.setting-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.setting-icon {
  font-size: 28px;
}

.texture-swatch {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  margin-right: 8px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  display: inline-block;
  flex-shrink: 0;
}
</style>
